import { Injectable } from '@angular/core';
import { FitApiClientService, FitList } from '@shared/fit-api-client.service';
import { unwrapAdminListItems } from '@shared/admin-list-response.util';
import { SortBy } from '@shared/components/list-sort/list-sort.component';

export interface ProgramCategoryListItem {
    id: number;
    name: string;
    is_published?: boolean;
    programs_count: number;
    order?: number;
}

interface ProgramCategory {
    id: number;
    name: string;
    description?: string;
    is_published?: boolean;
    order?: number;
    programs: ProgramListItem[];
}

export interface ProgramCategoryRequest {
    id: number;
    name: string;
    description?: string;
    is_published?: boolean;
    order?: number;
    program_ids: number[];
}

export interface ProgramListItem {
    id: number;
    name: string;
}

export interface ProgramCategorySearch {
    id: number;
    name: string;
}

export { FitList, ProgramCategory };

@Injectable({ providedIn: 'root' })

export class ProgramCategoryService {
    constructor(private fitApi: FitApiClientService) { }

    public search(query: string, page: number, order: SortBy = 'newest') {
        return this.fitApi.get<FitList<ProgramCategoryListItem>>('/admin/program-category', {
            search: query,
            order: order,
            page: page.toString()
        });
    }
    
    public get(id: number) {
        return this.fitApi.get<ProgramCategory>('/admin/program-category/' + id);
    }

    public create(programCategory: ProgramCategoryRequest) {
        return this.fitApi.put<ProgramCategory>('/admin/program-category/0', programCategory);
    }

    public update(programCategory: ProgramCategoryRequest) {
        return this.fitApi.put<ProgramCategory>('/admin/program-category/' + programCategory.id, programCategory);
    }

    public publish(programCategoryId: number) {
        return this.fitApi.put<any>(`/admin/program-category/${programCategoryId}/publish`, null);
    }

    public delete(id: number) {
        return this.fitApi.delete<any>('/admin/program-category/' + id);
    }

    public list() {
        return this.fitApi.get<Record<string, unknown>>('/admin/program-category/list').then(({ data, error }) => ({
            data: unwrapAdminListItems<ProgramCategoryListItem>(data, ['program_categories']),
            error,
        }));
    }

    public async all(): Promise<ProgramCategoryListItem[]> {
        const { data, error } = await this.list();
        if (error || !data) {
            return [];
        }

        return [...data].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.name.localeCompare(b.name));
    }
}
